import type { Cards } from '../cards/cards';
import { withHand } from '../cards/cards';
import { BlockHold } from './blockHold';
import { Bout } from '../rules/bout';
import { OpponentBrain, seededRandom } from '../rules/brain';
import { Fighter } from '../rules/fighter';
import type { FightCommand, PunchStats } from '../rules/types';
import { isPunch } from '../rules/types';

export type Submitted = 'done' | 'locked' | 'tired' | 'refused';

/** The player's punches: the jab, and what he owns of the rest. Hooks, uppercuts and body blows come with both hands. */
export function playerMoves(cards: Cards, owns: (id: string) => boolean): PunchStats[] {
  const p = cards.playerPunches;
  const moves = [p.jab];
  if (owns('straight')) moves.push(p.straight);
  for (const id of ['body', 'hook', 'uppercut'] as const) if (owns(id)) moves.push(withHand(p[id], 'left'), withHand(p[id], 'right'));
  if (owns('super')) moves.push(p.super);
  return moves;
}

/** One bout from the cards: the rules, the opponent's brain, and the player's commands and held block fed into them. */
export class FightSession {
  readonly bout: Bout;
  private readonly brain: OpponentBrain;
  private readonly block = new BlockHold();

  constructor(readonly cards: Cards, owns: (id: string) => boolean, seed = Date.now()) {
    const rules = cards.rules;
    this.bout = new Bout(rules, new Fighter(cards.playerHealth, rules, playerMoves(cards, owns)), new Fighter(cards.opponent.health, rules, Object.values(cards.opponentPunches)));
    this.brain = new OpponentBrain(this.bout, cards.opponent.brain, seededRandom(seed));
  }

  /** A punch he does not own yet is 'locked', one he has no stamina for is 'tired': the game shows which. */
  submit(command: FightCommand): Submitted {
    const player = this.bout.player;
    if (isPunch(command.action) && !player.knows(command.hand, command.action)) return 'locked';
    if (this.bout.submit('player', command)) return 'done';
    return isPunch(command.action) && this.bout.phase === 'round' && player.tooTiredFor(command.hand, command.action) ? 'tired' : 'refused';
  }

  step(seconds: number, blockHeld: boolean): void {
    this.block.sync(this.bout, blockHeld);
    this.brain.tick(seconds);
    this.bout.tick(seconds);
  }
}
